import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector, RootState } from '../../store';
import {
  fetchExpenses,
  createExpense,
  updateExpense,
  deleteExpense,
  approveExpense,
  writeOffExpense,
  fetchExpenseSummary,
} from '../../store/actions/FinanceActions';
import Table from '../shared/TailwindComponents/Table';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import Input from '../shared/TailwindComponents/Input';
import Textarea from '../shared/TailwindComponents/Textarea';
import ModalShell from '../shared/ModalShell';
import {
  PiPlusLight,
  PiCheckLight,
  PiTrashLight,
  PiProhibitLight,
  PiReceiptLight,
  PiPencilSimpleLight,
} from 'react-icons/pi';
import { FinanceExpense } from '../../store/reducer/FinanceReducer';

interface Props {
  organizationId: number | undefined;
}

const formatCurrency = (amount: number, currency: string = 'usd') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(amount / 100);
};

const statusBadgeColor: Record<string, any> = {
  pending: 'yellow',
  approved: 'green',
  invoiced: 'blue',
  written_off: 'gray',
};

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'filing_fees', label: 'Filing Fees' },
  { value: 'court_costs', label: 'Court Costs' },
  { value: 'travel', label: 'Travel' },
  { value: 'expert_witness', label: 'Expert Witness' },
  { value: 'copying', label: 'Copying & Printing' },
  { value: 'postage', label: 'Postage & Delivery' },
  { value: 'research', label: 'Research' },
  { value: 'technology', label: 'Technology' },
  { value: 'other', label: 'Other' },
];

const ExpensesTab: React.FC<Props> = ({ organizationId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const expenses = useAppSelector((state: RootState) => state.finance.expenses);
  const expenseSummary = useAppSelector((state: RootState) => state.finance.expenseSummary);
  const loading = useAppSelector((state: RootState) => state.finance.loading);

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState<FinanceExpense | null>(null);

  useEffect(() => {
    if (organizationId) {
      dispatch(fetchExpenses(organizationId, {
        page,
        status: statusFilter || undefined,
        category: categoryFilter || undefined,
      }));
      dispatch(fetchExpenseSummary(organizationId));
    }
  }, [dispatch, organizationId, page, statusFilter, categoryFilter]);

  // Listen for header "New Expense" button
  useEffect(() => {
    const handler = () => setShowCreateForm(true);
    window.addEventListener('finance:new-expense', handler);
    return () => window.removeEventListener('finance:new-expense', handler);
  }, []);

  const refreshList = () => {
    if (!organizationId) return;
    dispatch(fetchExpenses(organizationId, {
      page,
      status: statusFilter || undefined,
      category: categoryFilter || undefined,
    }));
    dispatch(fetchExpenseSummary(organizationId));
  };

  const handleApprove = async (id: number) => {
    if (!organizationId) return;
    await dispatch(approveExpense(id, organizationId));
    refreshList();
  };

  const handleWriteOff = async (id: number) => {
    if (!organizationId) return;
    await dispatch(writeOffExpense(id, organizationId));
    refreshList();
  };

  const handleDelete = async (id: number) => {
    if (!organizationId) return;
    await dispatch(deleteExpense(id, organizationId));
    refreshList();
  };

  const statusFilterOptions = [
    { value: '', label: t('All statuses') },
    { value: 'pending', label: t('Pending') },
    { value: 'approved', label: t('Approved') },
    { value: 'invoiced', label: t('Invoiced') },
    { value: 'written_off', label: t('Written off') },
  ];

  const columns = ['Date', 'Category', 'Description', 'Amount', 'Billable', 'Status'];

  const tableData = expenses.items.map((exp: FinanceExpense) => ({
    id: exp.id,
    Date: exp.expense_date ? new Date(exp.expense_date).toLocaleDateString() : new Date(exp.created_at).toLocaleDateString(),
    Category: exp.category,
    Description: exp.description || '—',
    Amount: exp.amount,
    Billable: exp.is_billable,
    Status: exp.status,
    _currency: exp.currency,
    _raw: exp,
  }));

  const renderCell = (column: string, value: any, row: any) => {
    switch (column) {
      case 'Category':
        return (
          <span className="text-gray-700 dark:text-gray-300 capitalize">
            {(value || '').replace(/_/g, ' ')}
          </span>
        );
      case 'Description':
        return (
          <span className="text-gray-500 dark:text-gray-400 max-w-xs truncate block">
            {value}
          </span>
        );
      case 'Amount':
        return (
          <span className="font-medium text-gray-900 dark:text-white">
            {formatCurrency(value, row._currency)}
          </span>
        );
      case 'Billable':
        return value ? (
          <Badge color="green">{t('Yes')}</Badge>
        ) : (
          <Badge color="gray">{t('No')}</Badge>
        );
      case 'Status':
        return (
          <Badge color={statusBadgeColor[value] || 'gray'}>
            {(value || '').replace(/_/g, ' ')}
          </Badge>
        );
      default:
        return value;
    }
  };

  const tableActions = [
    {
      label: t('Edit'),
      icon: <PiPencilSimpleLight className="w-4 h-4" />,
      onClick: (row: any) => setEditingExpense(row._raw),
      disabled: (row: any) => row._raw.status !== 'pending',
    },
    {
      label: t('Approve'),
      icon: <PiCheckLight className="w-4 h-4" />,
      onClick: (row: any) => handleApprove(row.id),
      disabled: (row: any) => row._raw.status !== 'pending',
    },
    {
      label: t('Write off'),
      icon: <PiProhibitLight className="w-4 h-4" />,
      onClick: (row: any) => handleWriteOff(row.id),
      disabled: (row: any) => !['pending', 'approved'].includes(row._raw.status),
    },
    {
      label: t('Delete'),
      icon: <PiTrashLight className="w-4 h-4" />,
      onClick: (row: any) => handleDelete(row.id),
      disabled: (row: any) => row._raw.status === 'invoiced',
      isDestructive: true,
    },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Summary cards */}
      {expenseSummary && (
        <div className="grid grid-cols-3 gap-4 px-4 pt-4">
          <SummaryCard
            label={t('Total expenses')}
            value={formatCurrency(expenseSummary.total_expenses, expenseSummary.currency)}
          />
          <SummaryCard
            label={t('Billable')}
            value={formatCurrency(expenseSummary.total_billable, expenseSummary.currency)}
            highlight
          />
          <SummaryCard
            label={t('Non-Billable')}
            value={formatCurrency(expenseSummary.total_non_billable, expenseSummary.currency)}
          />
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-3 px-4 py-3">
        <Dropdown
          options={statusFilterOptions}
          value={statusFilter}
          onChange={(val) => { setStatusFilter(val as string); setPage(1); }}
          placeholder={t('All statuses')}
          compact
          width="w-40"
        />
        <Dropdown
          options={CATEGORY_OPTIONS.map(c => ({ value: c.value, label: t(c.label) }))}
          value={categoryFilter}
          onChange={(val) => { setCategoryFilter(val as string); setPage(1); }}
          placeholder={t('All categories')}
          compact
          width="w-44"
        />
      </div>

      {/* Create / Edit Modal */}
      <ExpenseModal
        isOpen={showCreateForm || !!editingExpense}
        organizationId={organizationId!}
        expense={editingExpense}
        onClose={() => { setShowCreateForm(false); setEditingExpense(null); }}
        onSaved={() => {
          setShowCreateForm(false);
          setEditingExpense(null);
          refreshList();
        }}
      />

      {/* Table */}
      <Table
        columns={columns}
        data={tableData}
        renderCell={renderCell}
        actions={tableActions}
        emptyState={{
          icon: <PiReceiptLight className="w-10 h-10" />,
          headline: t('No expenses yet'),
        }}
      />

      {/* Pagination */}
      {expenses.total_pages > 1 && (
        <div className="flex items-center justify-between px-4 py-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('Page')} {expenses.page} {t('of')} {expenses.total_pages} · {expenses.total} {t('total')}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="small"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              {t('Previous')}
            </Button>
            <Button
              variant="outline"
              size="small"
              onClick={() => setPage((p) => Math.min(expenses.total_pages, p + 1))}
              disabled={page >= expenses.total_pages}
            >
              {t('Next')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Summary Card ───────────────────────────────────────────────

const SummaryCard: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className="glass rounded-lg p-4">
    <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
    <p className={`text-lg font-medium mt-1 ${highlight ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'}`}>
      {value}
    </p>
  </div>
);

// ── Expense Modal (Create / Edit) ──────────────────────────────

const EXPENSE_MODAL_CATEGORIES = [
  { value: 'filing_fees', label: 'Filing Fees' },
  { value: 'court_costs', label: 'Court Costs' },
  { value: 'travel', label: 'Travel' },
  { value: 'expert_witness', label: 'Expert Witness' },
  { value: 'copying', label: 'Copying & Printing' },
  { value: 'postage', label: 'Postage & Delivery' },
  { value: 'research', label: 'Research' },
  { value: 'technology', label: 'Technology' },
  { value: 'office_supplies', label: 'Office Supplies' },
  { value: 'meals_entertainment', label: 'Meals & Entertainment' },
  { value: 'software', label: 'Software & Subscriptions' },
  { value: 'professional_services', label: 'Professional Services' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'custom', label: 'Custom...' },
];

const ExpenseModal: React.FC<{
  isOpen: boolean;
  organizationId: number;
  expense: FinanceExpense | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ isOpen, organizationId, expense, onClose, onSaved }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state: RootState) => state.finance.loading);

  const isKnownCategory = (cat: string) =>
    EXPENSE_MODAL_CATEGORIES.some(c => c.value === cat && c.value !== 'custom');

  const [category, setCategory] = useState(expense?.category || 'filing_fees');
  const [customCategory, setCustomCategory] = useState(
    expense?.category && !isKnownCategory(expense.category) ? expense.category : ''
  );
  const [description, setDescription] = useState(expense?.description || '');
  const [amount, setAmount] = useState(expense ? (expense.amount / 100).toFixed(2) : '');
  const [isBillable, setIsBillable] = useState(expense?.is_billable ?? true);
  const [expenseDate, setExpenseDate] = useState(
    expense?.expense_date ? expense.expense_date.split('T')[0] : new Date().toISOString().split('T')[0]
  );

  // Reset form when expense changes
  useEffect(() => {
    if (isOpen) {
      const cat = expense?.category || 'filing_fees';
      const known = isKnownCategory(cat);
      setCategory(known ? cat : 'custom');
      setCustomCategory(known ? '' : cat);
      setDescription(expense?.description || '');
      setAmount(expense ? (expense.amount / 100).toFixed(2) : '');
      setIsBillable(expense?.is_billable ?? true);
      setExpenseDate(
        expense?.expense_date ? expense.expense_date.split('T')[0] : new Date().toISOString().split('T')[0]
      );
    }
  }, [isOpen, expense]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amountCents = Math.round(parseFloat(amount) * 100);
    if (!amountCents || amountCents <= 0) return;

    const resolvedCategory = category === 'custom' ? (customCategory.trim() || 'other') : category;

    const payload = {
      organization_id: organizationId,
      category: resolvedCategory,
      description: description || undefined,
      amount: amountCents,
      is_billable: isBillable,
      expense_date: expenseDate || undefined,
    };

    let result;
    if (expense) {
      result = await dispatch(updateExpense(expense.id, payload));
    } else {
      result = await dispatch(createExpense(payload));
    }

    if (result) {
      onSaved();
    }
  };

  const categoryOptions = EXPENSE_MODAL_CATEGORIES.map(c => ({
    value: c.value,
    label: t(c.label),
  }));

  if (!isOpen) return null;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={expense ? t('Edit expense') : t('New expense')}
      size="md"
      zIndexClassName="z-[9999999]"
      footer={
        <>
          <Button variant="outline" size="small" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button
            variant="primary"
            size="small"
            type="submit"
            form="expense-form"
            isLoading={loading?.createExpense || loading?.updateExpense}
          >
            {expense ? t('Save changes') : t('Add expense')}
          </Button>
        </>
      }
    >
        <form id="expense-form" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Dropdown
                options={categoryOptions}
                value={category}
                onChange={(val) => setCategory(val as string)}
                label={t('Category')}
              />
              <Input
                label={t('Amount ($)')}
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>

            {category === 'custom' && (
              <Input
                label={t('Custom category name')}
                type="text"
                value={customCategory}
                onChange={(e) => setCustomCategory(e.target.value)}
                placeholder={t('e.g., Courier Service, Mediation Fee...')}
              />
            )}

            <Textarea
              id="expense-description"
              label={t('Description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('Expense description...')}
              rows={2}
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label={t('Date')}
                type="date"
                value={expenseDate}
                onChange={(e) => setExpenseDate(e.target.value)}
              />
              <div className="flex items-center pt-6">
                <button
                  type="button"
                  onClick={() => setIsBillable(!isBillable)}
                  className="flex items-center gap-3 group cursor-pointer"
                >
                  <div className={`relative w-9 h-5 rounded-full transition-colors duration-200 ${
                    isBillable ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}>
                    <div className={`absolute top-0.5 start-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${
                      isBillable ? 'translate-x-4' : 'translate-x-0'
                    }`} />
                  </div>
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('Billable')}</span>
                </button>
              </div>
            </div>
          </div>
        </form>
    </ModalShell>
  );
};

export default ExpensesTab;
